import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "../assets/css/style.css";

function AboutPage() {
  const { t, i18n } = useTranslation();
  const githubUsername = "DanilTop3245";

  const [githubContributions, setGithubContributions] = useState([]);
  const [loadingGithub, setLoadingGithub] = useState(true);

  const [availableYears, setAvailableYears] = useState(["2026", "2025", "2024", "2023"]);
  const [selectedYear, setSelectedYear] = useState("2026");

  useEffect(() => {
    fetch(`https://github-contributions-api.jogruber.de/v4/${githubUsername}`)
      .then((res) => {
        if (!res.ok) throw new Error("Network response was not ok");
        return res.json();
      })
      .then((data) => {
        if (data && data.contributions) {
          setGithubContributions(data.contributions);
          if (data.total) {
            const years = Object.keys(data.total).sort((a, b) => b - a);
            setAvailableYears(years);
            if (years.length > 0) {
              setSelectedYear(years[0]);
            }
          }
        }
        setLoadingGithub(false);
      })
      .catch((err) => {
        console.error("Error fetching GitHub contributions:", err);
        setLoadingGithub(false);
      });
  }, []);

  // Load intro paragraphs
  const introParagraphs = [
    t("about.p1"),
    t("about.p2"),
    t("about.p3"),
    t("about.p4")
  ].filter(Boolean);

  // Load stats
  const stats = [
    {
      value: t("about.stats.years_value"),
      label: t("about.stats.years_label"),
      color: "var(--accent-2)"
    },
    {
      value: t("about.stats.projects_value"),
      label: t("about.stats.projects_label"),
      color: "var(--accent-1)"
    },
    {
      value: t("about.stats.skills_value"),
      label: t("about.stats.skills_label"),
      color: "var(--accent-3)"
    }
  ];

  // Load timeline items and enrich them with statuses & grid classes
  const timelineItemsRaw = t("about.timeline.items", { returnObjects: true }) || [];
  
  const timelineMeta = [
    {
      status: t("contacts.form_success_title") ? "В процессе" : "In Progress",
      statusClass: "status-active",
      gridClass: "bento-timeline-1",
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
          <path d="M6 12v5c0 2 2 3 6 3s6-1 6-3v-5"/>
        </svg>
      )
    },
    {
      status: t("contacts.form_success_title") ? "Завершено" : "Completed",
      statusClass: "status-done",
      gridClass: "bento-timeline-2",
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20M4 19.5A2.5 2.5 0 0 0 6.5 22H20M4 19.5V3.5A2.5 2.5 0 0 1 6.5 1H20v16H6.5a2.5 2.5 0 0 0-2.5 2.5z"/>
          <path d="M9 6h6M9 10h6"/>
        </svg>
      )
    },
    {
      status: t("contacts.form_success_title") ? "Завершено" : "Completed",
      statusClass: "status-done",
      gridClass: "bento-timeline-3",
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
          <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
        </svg>
      )
    }
  ];

  const timelineItems = timelineItemsRaw.map((item, idx) => ({
    ...item,
    ...timelineMeta[idx]
  }));

  // Load skills
  const allSkills = t("about.skills", { returnObjects: true }) || [];
  const frontendSkills = allSkills.filter(s => 
    ["HTML", "CSS (LESS)", "Flexbox", "Bootstrap", "JavaScript", "React.js", "Vue.js", "Nuxt.js"].includes(s)
  );
  const backendSkills = allSkills.filter(s => 
    ["Node.js", "Python", "SQL", "C#"].includes(s)
  );
  const toolsSkills = allSkills.filter(s => 
    ["Wix Velo", "Git", "GitHub"].includes(s)
  );

  const languages = [
    { name: t("about.languages.ukrainian"), level: t("about.languages.ukrainian_level") },
    { name: t("about.languages.english"), level: t("about.languages.english_level") }
  ];

  // Reusable Mouse move 3D Tilt handler
  const handleMouseMove = (e) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const xc = rect.width / 2;
    const yc = rect.height / 2;
    
    // Smooth angle range (dividing factors can increase/decrease tilt intensity)
    const angleX = (yc - y) / 18; 
    const angleY = (x - xc) / 18;
    
    card.style.transform = `perspective(1000px) rotateX(${angleX}deg) rotateY(${angleY}deg) scale3d(1.015, 1.015, 1.015)`;
  };

  const handleMouseLeave = (e) => {
    const card = e.currentTarget;
    card.style.transform = `perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)`;
  };

  // Generate simulated or real GitHub active commits grid (aligned by weekday for full selected year)
  const renderGitHubContributions = () => {
    const rows = 7;
    const grid = [];
    const hasRealData = githubContributions && githubContributions.length > 0;

    if (loadingGithub) {
      return (
        <div className="github-grid-board-wrapper" style={{ display: "flex", flexDirection: "column", gap: "16px", width: "100%", minWidth: 0 }}>
          <div className="github-stats-summary-row" style={{ pointerEvents: "none" }}>
            {[1, 2, 3].map((idx) => (
              <div key={idx} className="github-stat-box">
                <span className="github-stat-num github-skeleton" style={{ height: "26px", width: "55px", display: "block" }}></span>
                <span className="github-stat-lbl github-skeleton" style={{ height: "12px", width: "80px", display: "block", marginTop: "4px" }}></span>
              </div>
            ))}
          </div>

          <div className="github-grid-board github-scrollable">
            <div className="github-days">
              <span>Mon</span>
              <span>Wed</span>
              <span>Fri</span>
            </div>
            <div className="github-board-container" style={{ minWidth: "max-content" }}>
              <div className="github-months-labels" style={{ position: "relative", height: "16px", marginBottom: "4px", width: "100%" }}>
                {["Jan", "Mar", "May", "Jul", "Sep", "Nov"].map((m, idx) => (
                  <span
                    key={idx}
                    className="github-skeleton"
                    style={{
                      position: "absolute",
                      left: `${idx * 16}%`,
                      width: "24px",
                      height: "9px",
                      borderRadius: "2px"
                    }}
                  />
                ))}
              </div>
              <div className="github-cells-grid">
                {Array.from({ length: 7 }).map((_, rIdx) => (
                  <div key={rIdx} className="github-cells-row">
                    {Array.from({ length: 53 }).map((_, cIdx) => (
                      <span
                        key={cIdx}
                        className="github-cell github-skeleton"
                      />
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      );
    }

    let dataToUse = [];

    if (hasRealData) {
      // Filter contributions by selected year
      dataToUse = githubContributions.filter((day) => day.date.startsWith(selectedYear));
    } else {
      // Generate a simulated year (365 days) chronologically
      const yearInt = parseInt(selectedYear) || 2026;
      const startDate = new Date(`${yearInt}-01-01`);
      const endDate = new Date(`${yearInt}-12-31`);
      
      let cur = new Date(startDate);
      while (cur <= endDate) {
        const dateStr = cur.toISOString().split("T")[0];
        const isWeekend = cur.getDay() === 0 || cur.getDay() === 6;
        let level = 0;
        if (!isWeekend && Math.random() > 0.4) {
          level = Math.floor(Math.random() * 4) + 1;
        } else if (isWeekend && Math.random() > 0.85) {
          level = Math.floor(Math.random() * 2) + 1;
        }
        dataToUse.push({
          date: dateStr,
          count: level * 3,
          level: level
        });
        cur.setDate(cur.getDate() + 1);
      }
    }

    if (dataToUse.length === 0) {
      return (
        <div style={{ fontSize: "12px", color: "var(--text-muted)", padding: "20px", fontFamily: "monospace" }}>
          {loadingGithub ? "Loading contributions..." : "No data available"}
        </div>
      );
    }

    // Sort chronologically
    dataToUse.sort((a, b) => new Date(a.date) - new Date(b.date));

    // Get weekday of the first element (0 = Sunday, 1 = Monday, ...)
    const firstWeekday = new Date(dataToUse[0].date).getDay();

    // Pad start with empty days so that first day sits on its correct weekday row (Sunday to Saturday)
    const padded = [];
    for (let i = 0; i < firstWeekday; i++) {
      padded.push({ level: 0, count: 0, date: "", isEmpty: true });
    }

    // Add days
    padded.push(...dataToUse);

    // Pad end to align to complete columns (multiple of 7)
    while (padded.length % 7 !== 0) {
      padded.push({ level: 0, count: 0, date: "", isEmpty: true });
    }

    // Group into columns (weeks)
    const colsCount = padded.length / 7;
    for (let r = 0; r < rows; r++) {
      const rowCells = [];
      for (let c = 0; c < colsCount; c++) {
        rowCells.push(padded[c * 7 + r]);
      }
      grid.push(rowCells);
    }

    // Dynamic month names with column positions
    const monthsWithIndices = [];
    let lastMonth = -1;
    for (let c = 0; c < colsCount; c++) {
      const day = grid[0][c]; // check the Sunday of week c
      if (day && day.date && !day.isEmpty) {
        try {
          const dateObj = new Date(day.date);
          const monthNum = dateObj.getMonth();
          if (monthNum !== lastMonth) {
            monthsWithIndices.push({
              name: dateObj.toLocaleString(i18n.language === 'uk' ? "uk-UA" : "en-US", { month: "short" }).replace(".", ""),
              colIndex: c
            });
            lastMonth = monthNum;
          }
        } catch (e) {}
      }
    }

    monthsWithIndices.forEach((m, idx) => {
      m.gapCount = idx;
    });

    // Calculate stats
    const totalCommits = dataToUse.reduce((acc, d) => acc + d.count, 0);
    const activeDays = dataToUse.filter((d) => d.count > 0).length;
    const maxSingleDay = dataToUse.length > 0 ? Math.max(...dataToUse.map((d) => d.count), 0) : 0;

    return (
      <div className="github-grid-board-wrapper" style={{ display: "flex", flexDirection: "column", gap: "16px", width: "100%", minWidth: 0 }}>
        {/* Real-time GitHub analytics dashboard */}
        <div className="github-stats-summary-row">
          <div className="github-stat-box">
            <span className="github-stat-num">{totalCommits}</span>
            <span className="github-stat-lbl">{t("about.stats.total_commits")}</span>
          </div>
          <div className="github-stat-box">
            <span className="github-stat-num">{activeDays}</span>
            <span className="github-stat-lbl">{t("about.stats.active_days")}</span>
          </div>
          <div className="github-stat-box">
            <span className="github-stat-num">{maxSingleDay}</span>
            <span className="github-stat-lbl">{t("about.stats.max_in_day")}</span>
          </div>
        </div>

        <div className="github-grid-board github-scrollable">
          <div className="github-days">
            <span>Mon</span>
            <span>Wed</span>
            <span>Fri</span>
          </div>
          <div className="github-board-container" style={{ minWidth: "max-content" }}>
            <div className="github-months-labels" style={{ position: "relative", height: "16px", marginBottom: "4px", width: "100%", minWidth: "max-content" }}>
              {monthsWithIndices.map((m, idx) => (
                <span
                  key={idx}
                  style={{
                    position: "absolute",
                    left: `${m.colIndex * 13.5 + m.gapCount * 12}px`,
                    color: "var(--text-muted)",
                    whiteSpace: "nowrap"
                  }}
                >
                  {m.name}
                </span>
              ))}
            </div>
            <div className="github-cells-grid">
              {grid.map((row, rIdx) => (
                <div key={rIdx} className="github-cells-row">
                  {row.map((day, cIdx) => {
                    const isMonthStart = monthsWithIndices.some(m => m.colIndex === cIdx && m.gapCount > 0);
                    return (
                    <span
                      key={cIdx}
                      className={`github-cell level-${day.level} ${day.isEmpty ? "empty-padding" : ""}`}
                      style={{
                        ...(day.isEmpty ? { opacity: 0, pointerEvents: "none" } : {}),
                        marginLeft: isMonthStart ? "12px" : "0px"
                      }}
                      title={day.date ? `${day.count} contributions on ${day.date}` : ""}
                    />
                  )})}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <section id="about" className="section reveal">
      
      {/* Bento Grid Layout */}
      <div className="bento-grid">
        
        {/* Bento Cell 1: Biography */}
        <div className="bento-card bento-bio">
          <div className="bento-bio-avatar-row">
            <div className="bento-avatar-wrapper">
              <svg viewBox="0 0 100 100" className="bento-avatar-icon">
                <defs>
                  <linearGradient id="avatarGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="var(--accent-2)" />
                    <stop offset="100%" stopColor="var(--accent-1)" />
                  </linearGradient>
                </defs>
                <circle cx="50" cy="50" r="48" fill="none" stroke="url(#avatarGrad)" strokeWidth="2" strokeDasharray="6 3" />
                <path d="M50 25a15 15 0 100 30 15 15 0 000-30z" fill="url(#avatarGrad)" />
                <path d="M50 62c-20 0-35 10-35 22h70c0-12-15-22-35-22z" fill="url(#avatarGrad)" />
              </svg>
            </div>
            <div>
              <h2 className="gradient-text" style={{ fontSize: "2rem", margin: 0 }}>
                {t("about.heading")}
              </h2>
              <span className="bento-subtitle">{t("home.status")}</span>
            </div>
          </div>
          <div className="about-text" style={{ marginTop: "16px" }}>
            {introParagraphs.map((p, i) => {
              const parts = p.split(/(\*\*.*?\*\*)/g);
              return (
                <p key={i} style={{ marginBottom: "12px", fontSize: "14px", lineHeight: "1.6" }}>
                  {parts.map((part, idx) => {
                    if (part.startsWith("**") && part.endsWith("**")) {
                      return <strong key={idx}>{part.slice(2, -2)}</strong>;
                    }
                    return part;
                  })}
                </p>
              );
            })}
          </div>
        </div>

        {/* Bento Cell 2: Stats */}
        <div className="bento-card bento-stats-card">
          <h4 className="bento-card-title">{t("about.timeline.title")} Stats</h4>
          <div className="bento-stats-list">
            {stats.map((stat, idx) => (
              <div key={idx} className="bento-stat-item" style={{ "--stat-color": stat.color }}>
                <span className="bento-stat-value">{stat.value}</span>
                <span className="bento-stat-label">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bento Cell 3: IT Step */}
        <div className={`bento-card bento-timeline-card ${timelineItems[0].gridClass}`}>
          <div className="bento-timeline-header">
            <div className="bento-timeline-icon">
              {timelineItems[0].icon}
            </div>
            <span className={`roadmap-status-badge ${timelineItems[0].statusClass}`}>
              {timelineItems[0].status}
            </span>
          </div>
          <div className="bento-timeline-meta" style={{ marginTop: "16px" }}>
            <span className="roadmap-period">{timelineItems[0].period}</span>
            <h4 className="roadmap-title" style={{ marginTop: "6px" }}>{timelineItems[0].title}</h4>
            <span className="roadmap-subtitle">{timelineItems[0].subtitle}</span>
          </div>
          <p className="roadmap-details" style={{ fontSize: "13px", marginTop: "12px", borderTop: "1px solid rgba(255,255,255,0.04)", paddingTop: "8px" }}>
            {timelineItems[0].details}
          </p>
        </div>

        {/* Bento Cell 4: Python Developer */}
        <div className={`bento-card bento-timeline-card ${timelineItems[1].gridClass}`}>
          <div className="bento-timeline-header">
            <div className="bento-timeline-icon">
              {timelineItems[1].icon}
            </div>
            <span className={`roadmap-status-badge ${timelineItems[1].statusClass}`}>
              {timelineItems[1].status}
            </span>
          </div>
          <div className="bento-timeline-meta" style={{ marginTop: "16px" }}>
            <span className="roadmap-period">{timelineItems[1].period}</span>
            <h4 className="roadmap-title" style={{ marginTop: "6px" }}>{timelineItems[1].title}</h4>
            <span className="roadmap-subtitle">{timelineItems[1].subtitle}</span>
          </div>
          <p className="roadmap-details" style={{ fontSize: "13px", marginTop: "12px", borderTop: "1px solid rgba(255,255,255,0.04)", paddingTop: "8px" }}>
            {timelineItems[1].details}
          </p>
        </div>

        {/* Bento Cell 5: Davydov Consulting */}
        <div className={`bento-card bento-timeline-card ${timelineItems[2].gridClass}`}>
          <div className="bento-timeline-header">
            <div className="bento-timeline-icon">
              {timelineItems[2].icon}
            </div>
            <span className={`roadmap-status-badge ${timelineItems[2].statusClass}`}>
              {timelineItems[2].status}
            </span>
          </div>
          <div className="bento-timeline-meta" style={{ marginTop: "16px" }}>
            <span className="roadmap-period">{timelineItems[2].period}</span>
            <h4 className="roadmap-title" style={{ marginTop: "6px" }}>{timelineItems[2].title}</h4>
            <span className="roadmap-subtitle">{timelineItems[2].subtitle}</span>
          </div>
          <p className="roadmap-details" style={{ fontSize: "13px", marginTop: "12px", borderTop: "1px solid rgba(255,255,255,0.04)", paddingTop: "8px" }}>
            {timelineItems[2].details}
          </p>
        </div>

        {/* Bento Cell 6: GitHub Contributions Board */}
        <div className="bento-card bento-github-card col-span-2">
          <div className="bento-github-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
            <h4 className="bento-card-title">
              {loadingGithub
                ? "GitHub Activity"
                : githubContributions && githubContributions.length > 0
                ? "GitHub Activity"
                : "GitHub Activity (Simulated)"}
            </h4>
            <span className="bento-github-username" style={{ fontSize: "12px", color: "var(--accent-1)", fontWeight: 600 }}>@{githubUsername}</span>
          </div>
          
          <div className="bento-github-layout" style={{ maxWidth: "100%", minWidth: 0 }}>
            <div className="bento-github-board-side" style={{ minWidth: 0, width: "100%" }}>
              {renderGitHubContributions()}
              
              <div className="github-legend" style={{ display: "flex", alignSelf: "flex-start", alignItems: "center", gap: 6, fontSize: "10px", color: "var(--text-muted)", marginTop: "12px", paddingLeft: "42px" }}>
                <span>Less</span>
                <span className="github-cell level-0"></span>
                <span className="github-cell level-1"></span>
                <span className="github-cell level-2"></span>
                <span className="github-cell level-3"></span>
                <span className="github-cell level-4"></span>
                <span>More</span>
              </div>
            </div>
            
            <div className="bento-github-years-side">
              {availableYears.map((year) => (
                <button
                  key={year}
                  className={`github-year-btn ${selectedYear === year ? 'active' : ''}`}
                  disabled={loadingGithub}
                  onClick={() => setSelectedYear(year)}
                  style={loadingGithub ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                >
                  {year}
                </button>
              ))}
            </div>
          </div>

          {/* GitHub Profile Footer: Top Languages & Direct Link Button */}
          <div className="github-profile-footer" style={{ marginTop: "24px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "24px", borderTop: "1px solid rgba(99, 102, 241, 0.08)", paddingTop: "18px", width: "100%" }}>
            <div className="github-languages-stats" style={{ flex: 1, minWidth: "240px" }}>
              <span style={{ fontSize: "11px", fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: "8px" }}>
                {t("about.github.top_languages")}
              </span>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {(() => {
                  const currentLangs = [
                    { name: "JavaScript & React", percentage: 72, color: "var(--accent-2)" },
                    { name: "Node.js, Databases & AI", percentage: 18, color: "var(--accent-1)" },
                    { name: "HTML5, CSS3 & UI Layout", percentage: 10, color: "var(--accent-3)" }
                  ];

                  return currentLangs.map((lang, idx) => (
                    <div key={idx}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", color: "var(--text-primary)", fontWeight: 600 }}>
                        <span>{lang.name}</span>
                        <span>{lang.percentage}%</span>
                      </div>
                      <div style={{ width: "100%", height: "4px", background: "rgba(99, 102, 241, 0.08)", borderRadius: "2px", overflow: "hidden", marginTop: "3px" }}>
                        <div style={{ width: `${lang.percentage}%`, height: "100%", background: lang.color }}></div>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
            
            <a
              href={`https://github.com/${githubUsername}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-secondary github-profile-btn"
              style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "10px 18px", fontSize: "12px", height: "fit-content", flexShrink: 0, background: "rgba(99, 102, 241, 0.05)", border: "1px solid rgba(99, 102, 241, 0.12)" }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 .5A12 12 0 00.8 13.6c0 5.2 3.4 9.6 8.1 11.2.6.1.8-.3.8-.6v-2.2c-3.3.7-4-1.6-4-1.6-.5-1.2-1.3-1.6-1.3-1.6-1-.7.1-.7.1-.7 1.1.1 1.6 1.1 1.6 1.1 1.1 1.8 2.8 1.3 3.5 1 .1-.8.4-1.3.8-1.6-2.7-.3-5.6-1.4-5.6-6.3 0-1.4.5-2.5 1.2-3.4-.1-.3-.5-1.6.1-3.4 0 0 1-.3 3.4 1.2a11.7 11.7 0 016.2 0c2.4-1.5 3.4-1.2 3.4-1.2.6 1.8.2 3.1.1 3.4.7.9 1.2 2 1.2 3.4 0 4.9-2.9 6-5.7 6.3.4.4.8 1.1.8 2.2v3.2c0 .3.2.7.8.6 4.7-1.6 8.1-6 8.1-11.2A12 12 0 0012 .5z"/>
              </svg>
              {t("about.github.view_profile")}
            </a>
          </div>
        </div>

        {/* Bento Cell 7: Full Tech Stack */}
        <div className="bento-card bento-skills-card">
          <h3 className="bento-card-title" style={{ marginBottom: "16px" }}>
            {t("about.skills_heading")}
          </h3>
          <div className="bento-skills-categories">
            <div className="bento-skill-group">
              <h5>Frontend</h5>
              <div className="skills-tags">
                {frontendSkills.map((s, idx) => (
                  <span key={idx} className="skill-tag">{s}</span>
                ))}
              </div>
            </div>

            <div className="bento-skill-group">
              <h5>Backend & DB</h5>
              <div className="skills-tags">
                {backendSkills.map((s, idx) => (
                  <span key={idx} className="skill-tag">{s}</span>
                ))}
              </div>
            </div>

            <div className="bento-skill-group">
              <h5>Tools & APIs</h5>
              <div className="skills-tags">
                {toolsSkills.map((s, idx) => (
                  <span key={idx} className="skill-tag">{s}</span>
                ))}
              </div>
            </div>

            <div className="bento-skill-group">
              <h5>{t("about.languages_heading")}</h5>
              <div className="skills-tags">
                {languages.map((l, idx) => (
                  <span key={idx} className="skill-tag">
                    {l.name}: <strong>{l.level}</strong>
                  </span>
                ))}
              </div>
            </div>
          </div>

        </div>

      </div>

      {/* Section Transition Separator */}
      <div className="section-divider"></div>

      {/* Core Expertise Block */}
      <div className="services-section">
        <h3 className="section-title text-center gradient-text" style={{ marginBottom: "36px", fontSize: "1.8rem" }}>
          {t("services.heading")}
        </h3>
        <div className="services-grid">
          
          <div className="glass-card service-card" onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
            <div className="service-icon-wrapper font-color-1">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 2 7 12 12 22 7 12 2"/>
                <polyline points="2 17 12 22 22 17"/>
                <polyline points="2 12 12 17 22 12"/>
              </svg>
            </div>
            <h4>{t("services.frontend_title")}</h4>
            <p>{t("services.frontend_desc")}</p>
          </div>

          <div className="glass-card service-card" onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
            <div className="service-icon-wrapper font-color-2">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <ellipse cx="12" cy="5" rx="9" ry="3"/>
                <path d="M3 5V19A9 3 0 0 0 21 19V5"/>
                <path d="M3 12A9 3 0 0 0 21 12"/>
              </svg>
            </div>
            <h4>{t("services.backend_title")}</h4>
            <p>{t("services.backend_desc")}</p>
          </div>

          <div className="glass-card service-card" onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
            <div className="service-icon-wrapper font-color-3">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
              </svg>
            </div>
            <h4>{t("services.integrations_title")}</h4>
            <p>{t("services.integrations_desc")}</p>
          </div>

        </div>
      </div>

    </section>
  );
}

export default AboutPage;
